import { useMutation, useQuery } from '@tanstack/react-query';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';

import { analytics } from 'analytics';
import Alert from 'ui/Alert';
import Button from 'ui/Button';
import Field from 'ui/Field';
import FileUpload, { UseFileUploadReturn } from 'ui/FileUpload';
import { VStack } from 'ui/Layout';
import Select, { createSelectCollection } from 'ui/Select';
import { toaster } from 'ui/Toaster';
import { Text } from 'ui/Typography';
import { useTimeMeasurement } from 'utils/time';

import { ASYNC_TASK_POLL_INTERVAL_MS } from '../../../constants';
import { useApiContext } from '../../../contexts/apiContext';
import { Sankey } from '../../../generated-api';
import { base64ToFile } from '../../../utils/image';
import { fileUploadProps } from './constants';

// TODO: Make this equal to StatementTypeEnum once we have other statement types supported
type AllowedStatementType = 'income' | 'other';

const supportedStatementTypes = createSelectCollection<AllowedStatementType>([
  {
    value: 'income',
    label: 'Income statement',
  },
  {
    value: 'other',
    label: 'Other',
  },
]);

type Props = {
  fileUpload: UseFileUploadReturn;
  onGeneratedDraft: (sankey: Sankey) => void;
};

const ImportFilesUploadPage: React.FC<Props> = ({
  fileUpload,
  onGeneratedDraft,
}) => {
  const { api } = useApiContext();
  const draftGenerationTimer = useTimeMeasurement();

  const [taskId, setTaskId] = useState('');
  const [hasCroppedPDF, setHasCroppedPDF] = useState(false);
  const [statementType, setStatementType] =
    useState<AllowedStatementType>('income');

  // For the most part we're only intereseted in uploaded images
  // (the PDF files are parsed into images and then discarded)
  const imageFiles = fileUpload.acceptedFiles.filter((file) =>
    file.type.startsWith('image/')
  );
  const hasImageFiles = imageFiles.length > 0;
  const remainingImageSlots = fileUploadProps.maxFiles - imageFiles.length;

  // Removed cropped PDF message when all images are removed
  useEffect(() => {
    if (!hasImageFiles) {
      setHasCroppedPDF(false);
    }
  }, [hasImageFiles]);

  // Uses our own API endpoint to parse a PDF file into a list of PNG images
  const { mutate: parsePDFFile, isLoading: isParsingPDF } = useMutation(
    async (pdfFile: File) => {
      try {
        // Delete the uploaded PDF file from the list right away
        fileUpload.deleteFile(pdfFile);
        // Parse the PDF file into PNG images using our own API endpoint
        const { data } = await api().utils.convertPdfToPngs(
          { pdfFile },
          // NOTE: For some reason the generated API doesn't do this automatically.
          // See https://github.com/sankeyart/sankeyart/issues/1944
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        analytics.track('Smart Import Dialog - Upload PDF', {
          pages: data.page_images.length,
        });
        // Show a warning message if the uploaded PDF file has been cropped
        if (data.page_images.length > remainingImageSlots) {
          setHasCroppedPDF(true);
        }
        // Add the generated PNG images to the list of uploaded files
        // (up to the maximum number of files allowed), converting their
        // returned base64 representation into real File objects
        fileUpload.setFiles(
          data.page_images
            .slice(0, Math.max(0, remainingImageSlots))
            .map((image) => base64ToFile(image, `${uniqueId()}.png`))
        );
      } catch (e: any) {
        // Show a specific error message if the uploaded PDF file is too large
        if (e?.response?.status === 413) {
          toaster.error({ title: e.response.data.message });
        } else {
          analytics.reportError('Error parsing uploaded PDF file', {
            error: e,
          });
        }
      }
    }
  );

  // Show an error message for each rejected file
  useEffect(() => {
    if (fileUpload.rejectedFiles.length > 0) {
      for (const file of fileUpload.rejectedFiles) {
        toaster.error({
          title: file.errors.includes('FILE_TOO_LARGE')
            ? 'Uploaded file exceeds 20MB'
            : 'Error uploading file',
        });
      }
      fileUpload.clearRejectedFiles();
    }
  }, [fileUpload.rejectedFiles]);

  // Whenever a new PDF is uploaded, parse it into PNG images using our own API
  // endpoint and replace the uploaded PDF with the generated PNG images.
  useEffect(() => {
    for (const file of fileUpload.acceptedFiles) {
      if (file.type === 'application/pdf') {
        parsePDFFile(file);
      }
    }
  }, [fileUpload.acceptedFiles]);

  // Schedules the generation of a Sankey diagram draft using our own API endpoint
  const { mutate: scheduleDraftGeneration, isLoading: isScheduling } =
    useMutation(async () => {
      try {
        const { data } = await api().ai.generateSankeyFromImages(
          { statementType, imageFiles },
          // NOTE: For some reason the generated API doesn't do this automatically.
          // See https://github.com/sankeyart/sankeyart/issues/1944
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setTaskId(data.task_id);
        draftGenerationTimer.start();
      } catch (error) {
        analytics.reportError('Error scheduling Sankey draft generation', {
          error,
        });
      }
    });

  // Polls the status of the Sankey diagram draft generation task using our own API
  // endpoint and calls the provided callback function when the task is completed.
  // TODO: Consider generalizing the async task polling logic and merging this with the mutation above.
  useQuery({
    queryKey: ['sankey-generation-status', taskId],
    queryFn: async () => {
      try {
        const { data } = await api().ai.sankeyGenerationStatus({ taskId });
        if (data.result && data.status === 'SUCCESS') {
          analytics.track('Smart Import Dialog - Generate Draft - Finish', {
            durationSeconds: draftGenerationTimer.stop(),
            pages: imageFiles.length,
            statementType,
          });
          onGeneratedDraft(data.result);
          setTaskId('');
        } else if (data.status === 'FAILURE') {
          throw new Error(data.error ?? undefined);
        }
        return data; // This is just to satisfy useQuery's return type
      } catch (error) {
        draftGenerationTimer.stop(); // Reset timer on error
        analytics.reportError('Error generating Sankey draft', { error });
        setTaskId('');
      }
    },
    refetchInterval: ASYNC_TASK_POLL_INTERVAL_MS,
    enabled: !!taskId, // Only poll when a task ID is set
  });

  return (
    <VStack alignItems="stretch" gap={3} w="100%">
      <Field label="Statement type">
        <Select
          value={[statementType]}
          onValueChange={({ value }) =>
            setStatementType(value[0] as AllowedStatementType)
          }
          collection={supportedStatementTypes}
        />
      </Field>
      <FileUpload.Root
        fileUpload={fileUpload}
        isLimitReached={imageFiles.length >= fileUploadProps.maxFiles}
        isProcessing={isParsingPDF}
      >
        <FileUpload.Dropzone
          description={
            <>
              <b>.png</b> or <b>.pdf</b> up to 20MB /{' '}
              {window.env.OPENAI_IMAGES_HARD_LIMIT} pages
            </>
          }
        />
        <FileUpload.ClipboardInput
          placeholder="Or paste your screenshots here..."
          onPaste={() => analytics.track('Smart Import Dialog - Paste Image')}
        />
        {imageFiles.length > window.env.OPENAI_IMAGES_SUGGESTED_LIMIT && (
          <Alert
            title={
              hasCroppedPDF
                ? 'Your uploaded PDF has been cropped'
                : 'Consider reducing uploaded assets'
            }
            status="warning"
            size="sm"
          >
            For optimal results, we recommend uploading smaller PDFs or removing
            all non-essential images below. You can also clear uploads and paste
            screenshots of your data directly in the input above.
          </Alert>
        )}
        <FileUpload.ImagePreviews imageFiles={imageFiles} />
      </FileUpload.Root>
      <Button
        variant="primary-solid"
        disabled={!hasImageFiles || isParsingPDF}
        loading={isScheduling || !!taskId}
        onClick={() => {
          scheduleDraftGeneration();
          analytics.track('Smart Import Dialog - Generate Draft - Click', {
            pages: imageFiles.length,
            statementType,
          });
        }}
      >
        Generate diagram draft
      </Button>
      {hasImageFiles && (
        <Text color="gray.600" fontSize="xs" mt={-2} textAlign="center">
          * By clicking, you agree to share your files with OpenAI. This may
          take up to a minute.
        </Text>
      )}
    </VStack>
  );
};

export default ImportFilesUploadPage;
